<template>
  <BasePage data-test-id="selfSchedulerView" :title-suffix="store.categoryTitle">
    <v-container class="calendar" fluid>
      <v-row style="margin-bottom: -1rem">
        <v-col cols="12" xs="12" sm="12" md="6" lg="5" xl="4" xxl="4">
          <h1 class="welcome">
            {{ t('text.selfScheduling.welcome') }} {{ store.person?.firstName }},
          </h1>
          <p>{{ t('text.selfScheduling.intro', { category: store.categoryTitle }) }}</p>
          <!--
          <PrimarySelect
            class="selector"
            v-model="settingsStore.localeCode"
            :label="t('labels.activeLanguage')"
            :items="settingsStore.languageOptions"
          ></PrimarySelect>
          -->
          <PrimarySelect
            v-model="store.selectedLocation"
            :label="t('labels.location')"
            :items="store.locationValues"
            @update:model-value="onLocationChange"
          ></PrimarySelect>
          <p>
            {{
              t(
                'text.openSlots',
                {
                  category: store.categoryTitle,
                  dateRange: calendarStore.getDateTitle(settingsStore.locale)
                },
                store.totalOpenSlots
              )
            }}
          </p>
          <div v-if="store.appointments.length > 0">
            <v-divider class="divider" />
            <h3>{{ t('headers.currentScheduledAppointments') }}</h3>
            <v-data-table
              :items-per-page-options="itemsPerPageOptions"
              :items-per-page="itemsPerPageOptions[0].value"
              :items="store.appointments"
              :headers="headers"
              :hide-default-footer="store.appointments.length <= 5"
            />
          </div>
        </v-col>
        <v-col cols="12" xs="12" sm="12" md="6" lg="7" xl="8" xxl="8">
          <CalendarEvents
            ref="calendarEvents"
            @change="updateEvents"
            @event-selected="showEventDetails"
            :events="store.slots"
            :disable-filter="true"
          />
        </v-col>
      </v-row>
    </v-container>
    <EditSignup />
  </BasePage>
</template>

<script setup lang="ts">
// STORES, IMPORTS, & COMPOSABLES
import { isDefined } from '@/composables/utils'
import { CalendarView } from '@/enums/CalendarView'
import type { ICalendarRange } from '@/interfaces/ICalendarRange'
import { IItemsPerPageOption } from '@/interfaces/IItemsPerPageOption'
import type { EventRecord } from '@/models/EventRecord'
import { useSelfSchedulerStore } from '@/stores/db/SelfSchedulerStore'
import { useCalendarStore } from '@/stores/ui/CalendarStore'
import { useSettingsStore } from '@/stores/ui/SettingsStore'
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const calendarStore = useCalendarStore()
const settingsStore = useSettingsStore()
const store = useSelfSchedulerStore()
const { t } = useI18n({ useScope: 'global' })

// PROPS & EMITS

// REACTIVE VARIABLES
const calendarEvents = ref<any>()
const itemsPerPageOptions = ref<Array<IItemsPerPageOption>>([
  { title: '5', value: 5 },
  { title: '10', value: 10 },
  { title: '15', value: 15 },
  { title: '20', value: 20 },
  { title: '25', value: 25 },
  { title: '50', value: 50 }
])

// COMPUTED PROPERTIES
const headers = computed(() => {
  return store.headers.map((header: any) => {
    return { ...header, title: t(header.title!) }
  })
})

// LIFECYCLE HOOKS
onMounted(() => {
  calendarEvents.value.calendarControls.setView(CalendarView.Month)
})

// FUNCTIONS
const onLocationChange = async () => {
  const range = calendarEvents.value.calendarControls.getRange()
  if (isDefined(range)) {
    await store.loadSlots(range as ICalendarRange)
  }
}
const updateEvents = async (range: ICalendarRange) => {
  store.loadSlots(range)
}

const showEventDetails = (eventId: number) => {
  const event = store.slots.find((event: EventRecord) => {
    return event?.id === eventId
  })
  if (event) {
    store.selectedItem = { ...event }
    store.showEditDialog = true
  }
}
</script>

<style lang="scss" scoped>
.available-slots {
  margin: 0;
  padding: 0 0 1rem 0;
}

.calendar {
  margin: 0;
  padding: 0.5rem 0 0 0;
}

.divider {
  margin-bottom: 1rem;
}

.welcome {
  font-size: 1rem;
}
</style>
